import { useCallback, useEffect, useRef } from "react";

import { COLORS, FONTS } from "@/lib/constants";
import { tint } from "@/lib/yuoDesign";
import { useResponsive } from "@/lib/responsive";

const HL_KINDS = [
  { key: "feeling", color: "var(--lit-coral-13)", label: "feeling" },
  { key: "craft",   color: COLORS.diary,     label: "craft"   },
  { key: "growth",  color: COLORS.symbiosis, label: "growth"  },
];

/**
 * Three-color YUO highlighter for a plain <textarea>.
 *
 * Wrap the currently-selected text in `[[feeling:...]] / [[craft:...]] /
 * [[growth:...]]` tags. `HighlightedText` already renders these as coloured
 * spans, so no downstream changes are needed.
 *
 * `textareaRef` — ref to the <textarea> the toolbar decorates
 * `value`, `onChange` — controlled text
 * `testIdPrefix` — used to build data-testids (e.g. "diary" → diary-highlight-feeling)
 *
 * We remember the last-known selection in a ref so that touch users, who
 * often lose the textarea selection the moment they tap a button, still get
 * the correct wrap.
 */
export function HighlightToolbar({ textareaRef, value, onChange, testIdPrefix = "hl" }) {
  const { isPhone } = useResponsive();
  const lastSelRef = useRef({ start: 0, end: 0 });

  // Track selection whenever the user interacts with the textarea.
  useEffect(() => {
    const el = textareaRef?.current;
    if (!el) return;
    const remember = () => {
      lastSelRef.current = {
        start: el.selectionStart ?? 0,
        end: el.selectionEnd ?? 0,
      };
    };
    const events = ["select", "keyup", "mouseup", "touchend", "click"];
    events.forEach((ev) => el.addEventListener(ev, remember));
    return () => events.forEach((ev) => el.removeEventListener(ev, remember));
  }, [textareaRef, value]);

  const wrap = useCallback((kind) => {
    const el = textareaRef?.current;
    if (!el) return;
    // Prefer the live selection; fall back to the last remembered one.
    let start = el.selectionStart ?? 0;
    let end = el.selectionEnd ?? 0;
    if (start === end) {
      start = lastSelRef.current.start;
      end = lastSelRef.current.end;
    }
    if (start === end) {
      el.focus();
      return;
    }
    const before = value.slice(0, start);
    const chunk = value.slice(start, end);
    const after = value.slice(end);
    // Retag if the whole selection is already a tag.
    const stripped = chunk.replace(/^\s*\[\[(feeling|craft|growth):|\]\]\s*$/g, "");
    const insertion = `[[${kind}:${stripped.trim() || chunk.trim()}]]`;
    const next = before + insertion + after;
    onChange(next);
    requestAnimationFrame(() => {
      const contentStart = start + insertion.indexOf(":") + 1;
      const contentEnd = start + insertion.length - 2;
      el.focus();
      el.setSelectionRange(contentStart, contentEnd);
      lastSelRef.current = { start: contentStart, end: contentEnd };
    });
  }, [textareaRef, value, onChange]);

  const clear = useCallback(() => {
    const el = textareaRef?.current;
    if (!el) return;
    let start = el.selectionStart ?? 0;
    let end = el.selectionEnd ?? 0;
    if (start === end) {
      start = lastSelRef.current.start;
      end = lastSelRef.current.end;
    }
    if (start === end) return;
    const chunk = value.slice(start, end);
    const stripped = chunk.replace(/\[\[(feeling|craft|growth):([^\]]+)\]\]/g, "$2");
    if (stripped === chunk) return;
    const next = value.slice(0, start) + stripped + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start, start + stripped.length);
      lastSelRef.current = { start, end: start + stripped.length };
    });
  }, [textareaRef, value, onChange]);

  return (
    <div
      style={isPhone ? {
        display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 5, alignItems: "center",
      } : {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
      }}
      data-testid={`${testIdPrefix}-highlight-toolbar`}
    >
      {HL_KINDS.map((k) => (
        <button
          key={k.key}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onClick={() => wrap(k.key)}
          data-testid={`${testIdPrefix}-highlight-${k.key}`}
          aria-label={`Mark selection as ${k.label}`}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: isPhone ? "center" : undefined,
            gap: 6,
            padding: isPhone ? "0 8px" : "6px 12px",
            height: isPhone ? 34 : undefined,
            minWidth: 0,
            borderRadius: 999,
            border: isPhone ? "none" : `1px solid ${tint(k.color, "55")}`,
            boxShadow: isPhone ? `inset 0 0 0 1px ${tint(k.color, "73")}` : "none",
            background: `${tint(k.color, "18")}`,
            color: k.color,
            fontFamily: FONTS.body,
            fontSize: isPhone ? 10.5 : 12,
            fontWeight: 600,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 999, background: k.color }} />
          {k.label}
        </button>
      ))}
      <button
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
        onClick={clear}
        data-testid={`${testIdPrefix}-highlight-clear`}
        aria-label="Clear color on the selection"
        style={{
          padding: isPhone ? "0 8px" : "6px 12px",
          height: isPhone ? 34 : undefined,
          borderRadius: 999,
          border: isPhone ? "none" : `1px solid ${COLORS.border}`,
          boxShadow: isPhone ? `inset 0 0 0 1px ${COLORS.border}` : "none",
          background: "transparent",
          color: COLORS.textMuted,
          fontFamily: FONTS.body,
          fontSize: isPhone ? 10.5 : 12,
        }}
      >
        clear
      </button>
      {!isPhone && <div style={{ flex: 1 }} />}
      <span style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: isPhone ? 10 : 11, gridColumn: isPhone ? "1 / -1" : undefined }}>
        Select text, then pick a color
      </span>
    </div>
  );
}
